import { useCallback, useRef, useState } from 'react'
import { RiUpload2Line, RiFilePdfLine, RiImageLine, RiLoader4Line } from 'react-icons/ri'
import toast from 'react-hot-toast'
import { getCanvasInstance } from '@/lib/canvasInstance'
import { useEditorStore } from '@/stores/useEditorStore'
import styles from './ImagePanel.module.css'

export function ImagePanel() {
    const { pushUndo } = useEditorStore()
    const imageInputRef = useRef<HTMLInputElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isLoadingPdf, setIsLoadingPdf] = useState(false)

    const addImageToCanvas = useCallback(async (url: string) => {
        const canvas = getCanvasInstance()
        if (!canvas) return

        const fabric = await import('fabric')

        const img = await fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' })

        // Scale to fit within document page (use document dimensions, not viewport)
        const docWidth = canvas.width! / (canvas.getZoom() || 1)
        const docHeight = canvas.height! / (canvas.getZoom() || 1)
        const maxW = Math.min(docWidth * 0.8, 600)
        const maxH = Math.min(docHeight * 0.8, 800)
        const scale = Math.min(maxW / (img.width || 1), maxH / (img.height || 1), 1)
        img.scale(scale)
        img.set({ left: 50, top: 50 })

        canvas.add(img)
        canvas.setActiveObject(img)
        canvas.requestRenderAll()

        pushUndo(JSON.stringify(canvas.toJSON()))
    }, [pushUndo])

    const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files || files.length === 0) return

        Array.from(files).forEach((file) => {
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} no es una imagen valida`)
                return
            }

            const reader = new FileReader()
            reader.onload = (ev) => {
                const url = ev.target?.result as string
                addImageToCanvas(url)
            }
            reader.readAsDataURL(file)
        })

        e.target.value = ''
    }, [addImageToCanvas])

    const renderPdfPage = useCallback(async (pdfDoc: { getPage: (n: number) => Promise<unknown> }, pageNum: number): Promise<string> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const page = await pdfDoc.getPage(pageNum) as any
        const viewport = page.getViewport({ scale: 2 })

        const offscreen = document.createElement('canvas')
        offscreen.width = viewport.width
        offscreen.height = viewport.height
        const ctx = offscreen.getContext('2d')!

        await page.render({ canvasContext: ctx, viewport }).promise
        return offscreen.toDataURL('image/png')
    }, [])

    const handlePdfImport = useCallback(async (file: File) => {
        setIsLoadingPdf(true)
        try {
            const pdfjsLib = await import('pdfjs-dist')
            pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
                'pdfjs-dist/build/pdf.worker.min.mjs',
                import.meta.url,
            ).toString()

            const arrayBuffer = await file.arrayBuffer()
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
            const totalPages = pdf.numPages

            toast.success(`PDF cargado: ${totalPages} pagina${totalPages > 1 ? 's' : ''}`)

            // Render each page and add to canvas
            for (let i = 1; i <= totalPages; i++) {
                const dataUrl = await renderPdfPage(pdf, i)
                await addImageToCanvas(dataUrl)
            }
        } catch (err) {
            console.error('PDF import error:', err)
            toast.error('Error al importar el PDF')
        } finally {
            setIsLoadingPdf(false)
        }
    }, [addImageToCanvas, renderPdfPage])

    const handleFileImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files || files.length === 0) return

        Array.from(files).forEach((file) => {
            if (file.type.startsWith('image/') || file.type === 'image/svg+xml') {
                const reader = new FileReader()
                reader.onload = (ev) => {
                    addImageToCanvas(ev.target?.result as string)
                }
                reader.readAsDataURL(file)
            } else if (file.type === 'application/pdf') {
                handlePdfImport(file)
            } else {
                toast.error(`Formato no soportado: ${file.name}`)
            }
        })

        e.target.value = ''
    }, [addImageToCanvas, handlePdfImport])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const files = e.dataTransfer.files
        Array.from(files).forEach((file) => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onload = (ev) => {
                    addImageToCanvas(ev.target?.result as string)
                }
                reader.readAsDataURL(file)
            } else if (file.type === 'application/pdf') {
                handlePdfImport(file)
            }
        })
    }, [addImageToCanvas, handlePdfImport])

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    return (
        <div className={styles.panel}>
            <h3 className={styles.title}>Imagenes</h3>

            <div
                className={styles.dropZone}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => imageInputRef.current?.click()}
            >
                <RiUpload2Line className={styles.dropIcon} />
                <span className={styles.dropText}>Arrastra aqui</span>
                <span className={styles.dropHint}>o haz clic para subir</span>
            </div>

            <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
            />

            <div className={styles.divider} />

            <h3 className={styles.title}>Importar</h3>

            <div className={styles.importButtons}>
                <button
                    className={styles.importButton}
                    onClick={() => imageInputRef.current?.click()}
                >
                    <RiImageLine />
                    <span>Imagenes</span>
                    <span className={styles.formats}>JPG, PNG, SVG</span>
                </button>

                <button
                    className={styles.importButton}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoadingPdf}
                >
                    {isLoadingPdf ? <RiLoader4Line className={styles.spinning} /> : <RiFilePdfLine />}
                    <span>PDF</span>
                    <span className={styles.formats}>{isLoadingPdf ? 'Cargando...' : 'Documento'}</span>
                </button>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.svg"
                multiple
                onChange={handleFileImport}
                style={{ display: 'none' }}
            />
        </div>
    )
}
